import React, { useState, useEffect } from 'react';
import Header from '../../components/ui/Header';
import TaxOverview from './components/TaxOverview';
import TaxCalculationForm from './components/TaxCalculationForm';
import TaxSummary from './components/TaxSummary';
import TaxReportGenerator from './components/TaxReportGenerator';
import TransactionHistory from './components/TransactionHistory';

const TaxCalculator = () => {
  const [user] = useState({
    name: 'Alex Johnson',
    email: 'alex.johnson@example.com',
    subscriptionTier: 'pro'
  });

  const [taxYear, setTaxYear] = useState(new Date()?.getFullYear());
  const [filingStatus, setFilingStatus] = useState('single');
  const [transactions, setTransactions] = useState([]);
  const [taxCalculations, setTaxCalculations] = useState({
    shortTermGains: 0,
    longTermGains: 0,
    shortTermLosses: 0,
    longTermLosses: 0,
    netShortTerm: 0,
    netLongTerm: 0,
    totalTaxableGains: 0,
    estimatedTax: 0,
    washSaleAdjustments: 0
  });

  // Mock transaction data
  const mockTransactions = [
    {
      id: 1,
      date: '2024-01-15',
      type: 'buy',
      asset: 'BTC',
      assetType: 'crypto',
      quantity: 0.5,
      price: 42000,
      fee: 25,
      totalValue: 21025,
      costBasis: 21025,
      currentPrice: 45250,
      currentValue: 22625,
      unrealizedGainLoss: 1600,
      realized: false
    },
    {
      id: 2,
      date: '2024-03-20',
      type: 'sell',
      asset: 'ETH',
      assetType: 'crypto',
      quantity: 5,
      price: 3200,
      fee: 15,
      totalValue: 15985,
      costBasis: 12500,
      realizedGainLoss: 3470,
      holdingPeriod: 'long', // > 365 days
      realized: true,
      originalPurchaseDate: '2023-02-15'
    },
    {
      id: 3,
      date: '2024-06-10',
      type: 'sell',
      asset: 'AAPL',
      assetType: 'stock',
      quantity: 10,
      price: 195.50,
      fee: 5,
      totalValue: 1950,
      costBasis: 1800,
      realizedGainLoss: 145,
      holdingPeriod: 'short', // < 365 days
      realized: true,
      originalPurchaseDate: '2024-01-05'
    },
    {
      id: 4,
      date: '2024-08-15',
      type: 'sell',
      asset: 'TSLA',
      assetType: 'stock',
      quantity: 5,
      price: 220.00,
      fee: 5,
      totalValue: 1095,
      costBasis: 1200,
      realizedGainLoss: -110,
      holdingPeriod: 'short',
      realized: true,
      originalPurchaseDate: '2024-05-20'
    }
  ];

  // Tax calculation logic
  const calculateTaxImplications = (transactionData) => {
    let shortTermGains = 0;
    let longTermGains = 0;
    let shortTermLosses = 0;
    let longTermLosses = 0;

    transactionData?.forEach(transaction => {
      if (transaction?.realized && transaction?.realizedGainLoss) {
        const gainLoss = transaction?.realizedGainLoss;
        
        if (transaction?.holdingPeriod === 'long') {
          if (gainLoss > 0) {
            longTermGains += gainLoss;
          } else {
            longTermLosses += Math.abs(gainLoss);
          }
        } else {
          if (gainLoss > 0) {
            shortTermGains += gainLoss;
          } else {
            shortTermLosses += Math.abs(gainLoss);
          }
        }
      }
    });

    const netShortTerm = shortTermGains - shortTermLosses;
    const netLongTerm = longTermGains - longTermLosses;
    
    // Calculate total taxable gains (losses can offset gains)
    let totalTaxableGains = 0;
    if (netShortTerm > 0) totalTaxableGains += netShortTerm;
    if (netLongTerm > 0) totalTaxableGains += netLongTerm * 0.15; // Simplified 15% long-term rate

    // Apply loss offsetting rules
    if (netShortTerm < 0 && netLongTerm > 0) {
      totalTaxableGains = Math.max(0, netLongTerm + netShortTerm) * 0.15;
    } else if (netLongTerm < 0 && netShortTerm > 0) {
      totalTaxableGains = Math.max(0, netShortTerm + netLongTerm);
    }

    // Estimate tax based on filing status and income brackets
    const estimatedTaxRate = filingStatus === 'single' ? 0.22 : 0.20; // Simplified rates
    const estimatedTax = (netShortTerm > 0 ? netShortTerm * estimatedTaxRate : 0) + 
                        (netLongTerm > 0 ? netLongTerm * 0.15 : 0);

    return {
      shortTermGains,
      longTermGains,
      shortTermLosses,
      longTermLosses,
      netShortTerm,
      netLongTerm,
      totalTaxableGains,
      estimatedTax,
      washSaleAdjustments: 0 // Simplified - would need more complex logic
    };
  };

  const handleAddTransaction = (newTransaction) => {
    const transaction = {
      ...newTransaction,
      id: Date.now(),
      currentPrice: newTransaction?.price * 1.1, // Mock current price
      currentValue: newTransaction?.quantity * newTransaction?.price * 1.1,
      unrealizedGainLoss: newTransaction?.type === 'buy' ? 
        (newTransaction?.quantity * newTransaction?.price * 0.1) : 0,
      realized: newTransaction?.type === 'sell'
    };
    
    setTransactions(prev => [...prev, transaction]);
  };

  const handleDeleteTransaction = (transactionId) => {
    setTransactions(prev => prev?.filter(t => t?.id !== transactionId));
  };

  const handleLogin = () => {
    window.location.href = '/user-login';
  };

  const handleLogout = () => {
    window.location.href = '/user-login';
  };

  const handleUpgrade = () => {
    window.location.href = '/subscription-management';
  };

  useEffect(() => {
    // Initialize with mock data
    setTransactions(mockTransactions);
    document.title = 'Tax Calculator - CryptoPredict Pro';
  }, []);

  useEffect(() => {
    // Recalculate taxes when transactions change
    const calculations = calculateTaxImplications(transactions);
    setTaxCalculations(calculations);
  }, [transactions, filingStatus]);

  return (
    <div className="min-h-screen bg-background">
      <Header 
        user={user}
        onLogin={handleLogin}
        onLogout={handleLogout}
        onUpgrade={handleUpgrade}
      />
      
      <main className="pt-16">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
          {/* Page Header */}
          <div className="mb-8">
            <h1 className="text-3xl font-bold text-foreground mb-2">Tax Calculator</h1>
            <p className="text-muted-foreground">
              Calculate tax implications for your cryptocurrency and stock trading activities
            </p>
          </div>

          {/* Tax Settings */}
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6 mb-8">
            <div className="bg-card border border-border rounded-lg p-6">
              <h2 className="text-lg font-semibold text-card-foreground mb-4">Tax Settings</h2>
              <div className="space-y-4">
                <div>
                  <label className="block text-sm font-medium text-card-foreground mb-2">
                    Tax Year
                  </label>
                  <select
                    value={taxYear}
                    onChange={(e) => setTaxYear(parseInt(e?.target?.value))}
                    className="w-full px-3 py-2 border border-border rounded-md bg-background text-foreground"
                  >
                    <option value={2024}>2024</option>
                    <option value={2023}>2023</option>
                    <option value={2022}>2022</option>
                  </select>
                </div>
                
                <div>
                  <label className="block text-sm font-medium text-card-foreground mb-2">
                    Filing Status
                  </label>
                  <select
                    value={filingStatus}
                    onChange={(e) => setFilingStatus(e?.target?.value)}
                    className="w-full px-3 py-2 border border-border rounded-md bg-background text-foreground"
                  >
                    <option value="single">Single</option>
                    <option value="married_joint">Married Filing Jointly</option>
                    <option value="married_separate">Married Filing Separately</option>
                    <option value="head_of_household">Head of Household</option>
                  </select>
                </div>
              </div>
            </div>

            <TaxOverview taxCalculations={taxCalculations} />
          </div>

          {/* Main Content Grid */}
          <div className="grid grid-cols-1 xl:grid-cols-3 gap-8">
            {/* Left Column - Transaction Form and History */}
            <div className="xl:col-span-2 space-y-8">
              <TaxCalculationForm onAddTransaction={handleAddTransaction} />
              
              <TransactionHistory 
                transactions={transactions}
                onDeleteTransaction={handleDeleteTransaction}
              />
            </div>

            {/* Right Column - Summary and Report Generator */}
            <div className="xl:col-span-1 space-y-8">
              <TaxSummary 
                taxCalculations={taxCalculations}
                taxYear={taxYear}
                filingStatus={filingStatus}
              />
              
              <TaxReportGenerator 
                transactions={transactions}
                taxCalculations={taxCalculations}
                taxYear={taxYear}
                filingStatus={filingStatus}
              />
            </div>
          </div>
        </div>
      </main>
    </div>
  );
};

export default TaxCalculator;